import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Upload, FileText, Image, X, AlertCircle, Type, Brain, PenTool } from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import { useMarkingStore } from '../../stores/markingStore';
import { useLearnersStore } from '../../stores/learnersStore';

const ACCEPTED_TYPES = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
const IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
const ACCEPTED_EXTENSIONS = ['pdf', 'jpg', 'jpeg', 'png', 'webp'];
const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp'];

const getFileExtension = (file) => String(file?.name || '').split('.').pop().toLowerCase();
const isAccepted = (file) => ACCEPTED_TYPES.includes(file.type) || ACCEPTED_EXTENSIONS.includes(getFileExtension(file));
const isImage = (file) => IMAGE_TYPES.includes(file.type) || IMAGE_EXTENSIONS.includes(getFileExtension(file));
const isPdf = (file) => file.type === 'application/pdf' || getFileExtension(file) === 'pdf';

const MarkingUploadForm = ({ onJobCreated, needsProfileCompletion = false, onCompleteProfile }) => {
  const { user, userData } = useAuthStore();
  const {
    createMarkingJob,
    uploadFiles,
    startMarking,
    failJob,
    markingTasks,
    markingQuestions,
    error,
    clearError
  } = useMarkingStore();
  const { learners, loadLearners } = useLearnersStore();

  const [scriptFiles, setScriptFiles] = useState([]);
  const [memoFiles, setMemoFiles] = useState([]);
  const [memoMode, setMemoMode] = useState('file');
  const [memoText, setMemoText] = useState('');
  const [title, setTitle] = useState('');
  const [subject, setSubject] = useState('');
  const [grade, setGrade] = useState('');
  const [totalMarks, setTotalMarks] = useState('');
  const [learnerName, setLearnerName] = useState('');
  const [learnerId, setLearnerId] = useState('');
  const [selectedLearnerId, setSelectedLearnerId] = useState('');
  const [markerName, setMarkerName] = useState('');
  const [markerEmail, setMarkerEmail] = useState('');
  const [mode, setMode] = useState('single-learner');
  const [markingType, setMarkingType] = useState('ai-collaborative');
  const [paperFiles, setPaperFiles] = useState([]);
  const [selectedTaskId, setSelectedTaskId] = useState('');
  const [selectedQuestionId, setSelectedQuestionId] = useState('');
  const [batchLearnerSelections, setBatchLearnerSelections] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dragOver, setDragOver] = useState(null);
  const [fileValidationError, setFileValidationError] = useState('');

  const isBatchQuestionMode = mode === 'batch-question';

  useEffect(() => {
    if (user?.uid) {
      loadLearners(user.uid).catch((err) => {
        console.error('Failed to load learners:', err);
      });
    }
  }, [user?.uid, loadLearners]);

  useEffect(() => {
    if (!markerEmail && user?.email) setMarkerEmail(user.email);
  }, [user?.email, markerEmail]);

  useEffect(() => {
    if (markerName) return;
    const computedName = [userData?.firstName, userData?.lastName].filter(Boolean).join(' ').trim();
    const fallbackName = String(userData?.displayName || user?.displayName || '').trim();
    if (computedName) setMarkerName(computedName);
    else if (fallbackName) setMarkerName(fallbackName);
  }, [markerName, userData?.firstName, userData?.lastName, userData?.displayName, user?.displayName]);

  const selectedTask = useMemo(
    () => markingTasks.find((task) => task.id === selectedTaskId) || null,
    [markingTasks, selectedTaskId]
  );

  const taskQuestions = useMemo(() => {
    if (!selectedTask) return [];
    const questionIds = Array.isArray(selectedTask.questionIds) ? selectedTask.questionIds : [];
    return questionIds
      .map((id) => markingQuestions.find((question) => question.id === id))
      .filter(Boolean);
  }, [selectedTask, markingQuestions]);

  const selectedQuestion = useMemo(
    () => taskQuestions.find((question) => question.id === selectedQuestionId) || null,
    [taskQuestions, selectedQuestionId]
  );

  useEffect(() => {
    if (!selectedTaskId || taskQuestions.length === 0) {
      setSelectedQuestionId('');
      return;
    }
    if (!taskQuestions.some((question) => question.id === selectedQuestionId)) {
      setSelectedQuestionId(taskQuestions[0].id);
    }
  }, [selectedTaskId, selectedQuestionId, taskQuestions]);

  useEffect(() => {
    if (!isBatchQuestionMode) return;
    setSubject(selectedTask?.subject || '');
    setGrade(selectedTask?.grade || '');
  }, [isBatchQuestionMode, selectedTask?.subject, selectedTask?.grade]);

  useEffect(() => {
    if (!isBatchQuestionMode) {
      setBatchLearnerSelections([]);
      return;
    }
    setBatchLearnerSelections((prev) => {
      const next = scriptFiles.map((_, index) => prev[index] || '');
      return next;
    });
  }, [scriptFiles, isBatchQuestionMode]);

  const processIncomingFiles = useCallback((incomingFiles, existing, type = 'script') => {
    const imagesOnly = isBatchQuestionMode && type === 'script';
    const candidates = Array.from(incomingFiles);
    const valid = candidates.filter((file) => {
      if (!isAccepted(file)) return false;
      return imagesOnly ? isImage(file) : true;
    });

    const rejected = candidates.filter((file) => !valid.includes(file));
    let validationError = '';
    if (rejected.length > 0) {
      const names = rejected.map((file) => file.name || 'unnamed file').join(', ');
      validationError = imagesOnly
        ? `Only JPG, PNG, or WebP images are supported in Batch Question mode. Rejected: ${names}`
        : `Only PDF, JPG, PNG, or WebP files are supported. Rejected: ${names}`;
    }

    if (valid.length === 0) return { files: existing, validationError };

    if (!imagesOnly) {
      const pdf = valid.find(isPdf);
      if (pdf) return { files: [pdf], validationError };
      if (existing.length === 1 && isPdf(existing[0])) return { files: valid, validationError };
    }

    const names = new Set(existing.map((f) => f.name));
    const newFiles = valid.filter((f) => !names.has(f.name));
    return { files: [...existing, ...newFiles], validationError };
  }, [isBatchQuestionMode]);

  const handleDrop = useCallback((e, type) => {
    e.preventDefault();
    setDragOver(null);
    const files = Array.from(e.dataTransfer.files || []);
    if (!files.length) return;
    const existing = type === 'script' ? scriptFiles : memoFiles;
    const result = processIncomingFiles(files, existing, type);
    setFileValidationError(result.validationError);
    if (type === 'script') setScriptFiles(result.files);
    else setMemoFiles(result.files);
  }, [memoFiles, processIncomingFiles, scriptFiles]);

  const handleDragOver = useCallback((e, type) => {
    e.preventDefault();
    setDragOver(type);
  }, []);

  const handleFileSelect = (e, type) => {
    // FileList is live and is emptied when the input value is reset. Snapshot it
    // first so React never processes an already-cleared selection.
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    const existing = type === 'script' ? scriptFiles : memoFiles;
    const result = processIncomingFiles(files, existing, type);
    setFileValidationError(result.validationError);
    if (type === 'script') setScriptFiles(result.files);
    else setMemoFiles(result.files);
  };

  const removeFile = (type, index) => {
    if (type === 'script') {
      setScriptFiles((prev) => prev.filter((_, i) => i !== index));
      if (isBatchQuestionMode) setBatchLearnerSelections((prev) => prev.filter((_, i) => i !== index));
    }
    else setMemoFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const selectLearnerForSingleMode = (id) => {
    setSelectedLearnerId(id);
    const learner = learners.find((item) => item.id === id);
    if (!learner) {
      setLearnerName('');
      setLearnerId('');
      return;
    }
    const fullName = `${learner.name || ''} ${learner.surname || ''}`.trim();
    setLearnerName(fullName);
    setLearnerId(learner.id);
    if (!subject && learner.subjects?.length) setSubject(learner.subjects[0]);
    if (!grade && learner.grade) setGrade(learner.grade);
  };

  const hasMemo = markingType === 'manual'
    ? true
    : (memoMode === 'text' ? memoText.trim().length > 0 : memoFiles.length > 0);

  const totalSize = (files) => files.reduce((sum, f) => sum + f.size, 0);

  const resolveTaskData = () => {
    if (!selectedTask) return null;
    return {
      id: selectedTask.id,
      taskName: selectedTask.taskName || '',
      taskType: selectedTask.taskType || '',
      subject: selectedTask.subject || '',
      grade: selectedTask.grade || '',
      topics: Array.isArray(selectedTask.topics) ? selectedTask.topics : [],
      questionIds: Array.isArray(selectedTask.questionIds) ? selectedTask.questionIds : [],
      questions: taskQuestions,
      selectedQuestionId: selectedQuestion?.id || null,
      selectedQuestionLabel: selectedQuestion?.questionNumber || null
    };
  };

  const getBatchRows = () => {
    return scriptFiles
      .map((file, index) => {
        const assignedLearnerId = batchLearnerSelections[index] || '';
        const learner = learners.find((item) => item.id === assignedLearnerId);
        if (!learner) return null;
        return {
          index,
          file,
          learnerId: learner.id,
          learnerName: `${learner.name || ''} ${learner.surname || ''}`.trim(),
          learnerNumber: learner.id,
          learnerGrade: learner.grade || ''
        };
      })
      .filter(Boolean);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (needsProfileCompletion) {
      onCompleteProfile?.();
      return;
    }
    if (scriptFiles.length === 0 || !hasMemo || !user?.uid) return;

    if (isBatchQuestionMode && (!selectedTaskId || !selectedQuestionId)) {
      return;
    }

    const batchRows = isBatchQuestionMode ? getBatchRows() : [];
    if (isBatchQuestionMode && batchRows.length !== scriptFiles.length) {
      return;
    }
    if (isBatchQuestionMode && markingType === 'ai-collaborative' && new Set(batchRows.map((row) => row.learnerId)).size !== batchRows.length) {
      setFileValidationError('Assign each question image to a different learner. Combine continuation work into a single image for that learner.');
      return;
    }

    setIsSubmitting(true);
    clearError();

    try {
      const isManual = markingType === 'manual';
      const isTextMemo = !isManual && memoMode === 'text';
      const taskData = resolveTaskData();
      const sharedPayload = {
        userId: user.uid,
        title: title || `Marking - ${new Date().toLocaleDateString()}`,
        mode,
        subject: (isBatchQuestionMode ? (taskData?.subject || '') : subject).trim(),
        grade: (isBatchQuestionMode ? (taskData?.grade || '') : grade).trim(),
        totalMarks: totalMarks ? parseInt(totalMarks, 10) : null,
        markerName: markerName.trim() || null,
        markerEmail: markerEmail.trim() || null,
        markingType,
        taskId: selectedTaskId || null,
        questionId: selectedQuestionId || null,
        questionLabel: selectedQuestion?.questionNumber || null,
        taskData,
        ...(isTextMemo ? { memoText: memoText.trim() } : {})
      };

      if (markingType === 'ai-collaborative') {
        const jobId = await createMarkingJob({
          ...sharedPayload,
          learnerName: isBatchQuestionMode ? '' : learnerName.trim(),
          learnerId: isBatchQuestionMode ? null : learnerId.trim() || null,
          workflowSource: 'collaborative-ai-v1'
        });
        try {
          const { scriptPaths, memoPaths } = await uploadFiles(
            jobId, user.uid, scriptFiles, isTextMemo ? [] : memoFiles, 'processing',
            isBatchQuestionMode ? batchRows.map((row) => ({
              index: row.index, learnerId: row.learnerId, learnerName: row.learnerName,
              learnerNumber: row.learnerNumber, questionId: selectedQuestionId,
              questionLabel: selectedQuestion?.questionNumber || ''
            })) : [], paperFiles
          );
          // The callable persists failures and proposals; navigation stays responsive.
          startMarking(jobId, scriptPaths, memoPaths, isTextMemo ? memoText.trim() : null, markingType)
            .catch((err) => console.error('AI preparation failed:', err));
          onJobCreated?.(jobId);
        } catch (err) {
          await failJob(jobId, err.message || 'Upload failed');
          throw err;
        }
      } else if (isBatchQuestionMode && !isManual) {
        let firstJobId = null;

        for (const row of batchRows) {
          const childJobId = await createMarkingJob({
            ...sharedPayload,
            mode: 'single-learner',
            workflowSource: 'batch-question-ai',
            learnerName: row.learnerName,
            learnerId: row.learnerId,
            learnerNumber: row.learnerNumber,
            batchMeta: {
              taskId: selectedTaskId,
              questionId: selectedQuestionId,
              questionLabel: selectedQuestion?.questionNumber || null
            }
          });

          if (!firstJobId) firstJobId = childJobId;

          const { scriptPaths, memoPaths } = await uploadFiles(
            childJobId,
            user.uid,
            [row.file],
            isTextMemo ? [] : memoFiles,
            'processing',
            [{
              index: 0,
              learnerId: row.learnerId,
              learnerName: row.learnerName,
              learnerNumber: row.learnerNumber,
              questionId: selectedQuestionId,
              questionLabel: selectedQuestion?.questionNumber || ''
            }]
          );

          startMarking(childJobId, scriptPaths, memoPaths, isTextMemo ? memoText.trim() : null, markingType).catch((err) => {
            console.error('Batch AI marking failed to start:', err);
            failJob(childJobId, err.message || 'Failed to start marking');
          });
        }

        if (onJobCreated && firstJobId) onJobCreated(firstJobId);
      } else {
        const jobId = await createMarkingJob({
          ...sharedPayload,
          learnerName: learnerName.trim(),
          learnerId: learnerId.trim() || null,
          learnerNumber: learnerId.trim() || null,
          batchQueue: isBatchQuestionMode
            ? batchRows.map((row) => ({
                index: row.index,
                learnerId: row.learnerId,
                learnerName: row.learnerName,
                learnerNumber: row.learnerNumber,
                questionId: selectedQuestionId,
                questionLabel: selectedQuestion?.questionNumber || ''
              }))
            : []
        });

        const { scriptPaths, memoPaths } = await uploadFiles(
          jobId,
          user.uid,
          scriptFiles,
          isManual ? [] : (isTextMemo ? [] : memoFiles),
          isManual ? 'manual-review' : 'processing',
          isBatchQuestionMode
            ? batchRows.map((row) => ({
                index: row.index,
                learnerId: row.learnerId,
                learnerName: row.learnerName,
                learnerNumber: row.learnerNumber,
                questionId: selectedQuestionId,
                questionLabel: selectedQuestion?.questionNumber || ''
              }))
            : []
        );

        if (!isManual) {
          startMarking(jobId, scriptPaths, memoPaths, isTextMemo ? memoText.trim() : null, markingType).catch((err) => {
            console.error('Marking failed to start:', err);
            failJob(jobId, err.message || 'Failed to start marking');
          });
        }

        if (onJobCreated) onJobCreated(jobId);
      }
    } catch (err) {
      console.error('Submit failed:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderDropZone = (type, files, setFiles) => {
    const isOver = dragOver === type;
    const label = type === 'script'
      ? (isBatchQuestionMode ? 'Question Scripts (one per learner)' : 'Student Script')
      : 'Memorandum';
    const hasFiles = files.length > 0;
    const hasPdf = hasFiles && isPdf(files[0]);
    const hasImages = hasFiles && isImage(files[0]);

    return (
      <div
        onDrop={(e) => handleDrop(e, type)}
        onDragOver={(e) => handleDragOver(e, type)}
        onDragLeave={() => setDragOver(null)}
        className={`relative rounded-xl border-2 border-dashed p-5 transition-colors ${
          isOver
            ? 'border-sigma-500 bg-sigma-50'
            : hasFiles
              ? 'border-green-300 bg-green-50'
              : 'border-gray-300 hover:border-sigma-400 bg-gray-50/50'
        }`}
      >
        {hasFiles ? (
          <div>
            <div className="mb-2 flex items-center justify-between">
              <div className="flex items-center space-x-2">
                {hasPdf ? <FileText className="h-5 w-5 text-green-600" /> : <Image className="h-5 w-5 text-green-600" />}
                <span className="text-sm font-semibold text-gray-900">
                  {hasPdf ? '1 PDF file' : `${files.length} image file${files.length > 1 ? 's' : ''}`}
                </span>
                <span className="text-xs text-gray-500">({(totalSize(files) / 1024 / 1024).toFixed(2)} MB)</span>
              </div>
              <button type="button" onClick={() => setFiles([])} className="text-xs font-medium text-red-600 hover:text-red-700">
                Clear all
              </button>
            </div>
            <div className="max-h-32 space-y-1 overflow-y-auto">
              {files.map((file, idx) => (
                <div key={`${file.name}-${idx}`} className="flex items-center justify-between rounded bg-white px-2 py-1 text-xs">
                  <span className="max-w-[220px] truncate text-gray-700">{file.name}</span>
                  <button type="button" onClick={() => removeFile(type, idx)} className="ml-2 text-gray-400 hover:text-red-500">
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
            {(hasImages || (isBatchQuestionMode && type === 'script')) && (
              <label className="mt-2 inline-flex cursor-pointer items-center text-xs font-medium text-sigma-600 hover:text-sigma-700">
                <Upload className="mr-1 h-3 w-3" />
                Add more images
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  multiple
                  onChange={(e) => handleFileSelect(e, type)}
                  className="hidden"
                />
              </label>
            )}
          </div>
        ) : (
          <>
            <Upload className="mx-auto mb-2 h-8 w-8 text-gray-400" />
            <p className="mb-1 text-center text-sm font-semibold text-gray-700">{label}</p>
            <p className="text-center text-xs text-gray-500">
              {isBatchQuestionMode && type === 'script' ? 'Images only (JPG, PNG, WebP)' : 'PDF or images (JPG, PNG, WebP)'}
            </p>
            <p className="text-center text-xs text-gray-500">Drag and drop or click to browse</p>
            <input
              type="file"
              accept={isBatchQuestionMode && type === 'script' ? 'image/jpeg,image/png,image/webp' : 'application/pdf,image/jpeg,image/png,image/webp'}
              multiple
              onChange={(e) => handleFileSelect(e, type)}
              className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
            />
          </>
        )}
      </div>
    );
  };

  const isDisabled = needsProfileCompletion
    || scriptFiles.length === 0
    || !hasMemo
    || isSubmitting
    || (isBatchQuestionMode && (!selectedTaskId || !selectedQuestionId || getBatchRows().length !== scriptFiles.length));

  return (
    <form onSubmit={handleSubmit} className="space-y-6 rounded-2xl border border-gray-300/90 bg-white p-6 shadow-[inset_0_2px_5px_rgba(0,0,0,0.3),0_0_10px_rgba(255,255,255,0.2)]">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Create Marking Job</h2>
        <p className="mt-1 text-sm text-gray-600">
          Choose script or question workflow, then run AI or manual marking.
        </p>
      </div>

      {error && (
        <div className="flex items-center space-x-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {fileValidationError && (
        <div className="flex items-center space-x-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <span>{fileValidationError}</span>
        </div>
      )}

      {needsProfileCompletion && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="font-semibold">Profile completion required</p>
              <p className="mt-1 text-amber-800">
                Complete your profile before uploading files for marking. Tools remain available.
              </p>
            </div>
            <button
              type="button"
              onClick={() => onCompleteProfile?.()}
              className="shrink-0 rounded-md border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-900 hover:bg-amber-100"
            >
              Complete Profile
            </button>
          </div>
        </div>
      )}

      <fieldset disabled={needsProfileCompletion} className="space-y-6 disabled:opacity-60">
        <div className="rounded-xl border border-gray-200 p-4">
          <label className="mb-2 block text-sm font-semibold text-gray-800">Workflow</label>
          <div className="mb-4 flex gap-3">
            <button
              type="button"
              onClick={() => setMode('single-learner')}
              className={`rounded-lg px-4 py-2 text-sm font-medium ${
                mode === 'single-learner' ? 'bg-sigma-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Script by Learner
            </button>
            <button
              type="button"
              onClick={() => setMode('batch-question')}
              className={`rounded-lg px-4 py-2 text-sm font-medium ${
                mode === 'batch-question' ? 'bg-sigma-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Batch by Question
            </button>
          </div>

          <label className="mb-2 block text-sm font-semibold text-gray-800">Marking Method</label>
          <div className="mb-2 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => setMarkingType('ai-collaborative')}
              className={`inline-flex items-center rounded-lg px-4 py-2 text-sm font-medium ${
                markingType === 'ai-collaborative' ? 'bg-sigma-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Brain className="mr-1.5 h-4 w-4" /> Collaborative AI Marking
            </button>
            <button
              type="button"
              onClick={() => setMarkingType('manual')}
              className={`inline-flex items-center rounded-lg px-4 py-2 text-sm font-medium ${
                markingType === 'manual' ? 'bg-sigma-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <PenTool className="mr-1.5 h-4 w-4" /> Manual Marking
            </button>
          </div>

          {markingType === 'ai-collaborative' && (
            <div className="mt-4 space-y-3 text-sm text-gray-600">
              <p>Verify evidence and the memorandum, calibrate with reviewed examples, then review proposals by question. An assigned moderator approves the sample before you release results.</p>
              <p>Use at most 18 MB of source files per AI session. Split larger assessments into question batches.</p>
              <label className="block font-medium text-gray-800">
                Question paper (optional when the task or memorandum includes the questions)
                <input type="file" multiple accept="application/pdf,image/jpeg,image/png,image/webp" className="mt-2 block w-full text-sm"
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    if (files.every(isAccepted)) { setPaperFiles(files); setFileValidationError(''); }
                    else { setPaperFiles([]); setFileValidationError('The question paper must be PDF or image files.'); }
                  }} />
              </label>
              {paperFiles.length > 0 && <p>{paperFiles.map((f) => f.name).join(', ')}</p>}
            </div>
          )}

          {isBatchQuestionMode && (
            <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-700">Task</label>
                <select
                  value={selectedTaskId}
                  onChange={(e) => setSelectedTaskId(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white focus:border-sigma-500 focus:outline-none focus:ring-2 focus:ring-sigma-100"
                >
                  <option value="">Select task...</option>
                  {markingTasks.map((task) => (
                    <option key={task.id} value={task.id}>
                      {task.taskName} ({task.subject || '-'} | Grade {task.grade || '-'})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-700">Question</label>
                <select
                  value={selectedQuestionId}
                  onChange={(e) => setSelectedQuestionId(e.target.value)}
                  disabled={!selectedTaskId}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white disabled:bg-gray-100 focus:border-sigma-500 focus:outline-none focus:ring-2 focus:ring-sigma-100"
                >
                  <option value="">Select question...</option>
                  {taskQuestions.map((question) => (
                    <option key={question.id} value={question.id}>
                      Q{question.questionNumber} ({question.marks || 0})
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>

        <div className={`grid grid-cols-1 gap-4 ${markingType !== 'manual' ? 'md:grid-cols-2' : ''}`}>
          {renderDropZone('script', scriptFiles, setScriptFiles)}

          {markingType !== 'manual' && (
            <div>
              <div className="mb-2 flex items-center space-x-2">
                <span className="text-sm font-semibold text-gray-800">Memorandum</span>
                <div className="ml-auto flex rounded-md bg-gray-100 p-0.5">
                  <button
                    type="button"
                    onClick={() => setMemoMode('file')}
                    className={`rounded px-2.5 py-1 text-xs font-medium ${
                      memoMode === 'file' ? 'bg-white text-sigma-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <Upload className="mr-1 inline h-3 w-3" /> File
                  </button>
                  <button
                    type="button"
                    onClick={() => setMemoMode('text')}
                    className={`rounded px-2.5 py-1 text-xs font-medium ${
                      memoMode === 'text' ? 'bg-white text-sigma-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <Type className="mr-1 inline h-3 w-3" /> Paste Text
                  </button>
                </div>
              </div>
              {memoMode === 'file' ? (
                renderDropZone('memo', memoFiles, setMemoFiles)
              ) : (
                <textarea
                  value={memoText}
                  onChange={(e) => setMemoText(e.target.value)}
                  placeholder="Paste memorandum text here..."
                  className="h-40 w-full resize-y rounded-xl border border-gray-300 px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:border-sigma-500 focus:outline-none focus:ring-2 focus:ring-sigma-100"
                />
              )}
            </div>
          )}
        </div>

        {isBatchQuestionMode && scriptFiles.length > 0 && (
          <div className="rounded-xl border border-gray-200 p-4">
            <h3 className="mb-3 text-sm font-semibold text-gray-800">Assign Learners to Uploaded Question Scripts</h3>
            <div className="space-y-2 max-h-56 overflow-y-auto">
              {scriptFiles.map((file, index) => (
                <div key={`${file.name}-${index}`} className="grid grid-cols-1 md:grid-cols-2 gap-2 items-center border border-gray-200 rounded-lg p-2">
                  <div className="text-xs text-gray-700 truncate">{file.name}</div>
                  <select
                    value={batchLearnerSelections[index] || ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      setBatchLearnerSelections((prev) => {
                        const next = [...prev];
                        next[index] = value;
                        return next;
                      });
                    }}
                    className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm bg-white focus:border-sigma-500 focus:outline-none"
                  >
                    <option value="">Select learner...</option>
                    {learners.map((learner) => (
                      <option key={learner.id} value={learner.id}>
                        {`${learner.name || ''} ${learner.surname || ''}`.trim()} (Grade {learner.grade || '-'})
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="rounded-xl border border-gray-200 p-4">
          <h3 className="mb-3 text-sm font-semibold text-gray-800">Job and Marker Information</h3>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700">Job Title</label>
              <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g., Grade 12 Maths Paper 1" className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-sigma-500 focus:outline-none focus:ring-2 focus:ring-sigma-100" />
            </div>

            {!isBatchQuestionMode && (
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-700">Learner</label>
                <select
                  value={selectedLearnerId}
                  onChange={(e) => selectLearnerForSingleMode(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white focus:border-sigma-500 focus:outline-none focus:ring-2 focus:ring-sigma-100"
                >
                  <option value="">Select learner...</option>
                  {learners.map((learner) => (
                    <option key={learner.id} value={learner.id}>
                      {`${learner.name || ''} ${learner.surname || ''}`.trim()} (Grade {learner.grade || '-'})
                    </option>
                  ))}
                </select>
              </div>
            )}

            {!isBatchQuestionMode && (
              <>
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-700">Learner Name</label>
                  <input value={learnerName} onChange={(e) => setLearnerName(e.target.value)} placeholder="e.g., Jane Doe" className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-sigma-500 focus:outline-none focus:ring-2 focus:ring-sigma-100" />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-700">Learner Identifier</label>
                  <input value={learnerId} onChange={(e) => setLearnerId(e.target.value)} placeholder="Student number / email / ID" className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-sigma-500 focus:outline-none focus:ring-2 focus:ring-sigma-100" />
                </div>
              </>
            )}

            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700">Marker Name</label>
              <input value={markerName} onChange={(e) => setMarkerName(e.target.value)} placeholder="Teacher / marker name" className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-sigma-500 focus:outline-none focus:ring-2 focus:ring-sigma-100" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700">Marker Email</label>
              <input type="email" value={markerEmail} onChange={(e) => setMarkerEmail(e.target.value)} placeholder="marker@school.org" className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-sigma-500 focus:outline-none focus:ring-2 focus:ring-sigma-100" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700">Subject</label>
              <input value={subject} onChange={(e) => setSubject(e.target.value)} disabled={isBatchQuestionMode} placeholder="e.g., Mathematics" className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-100 focus:border-sigma-500 focus:outline-none focus:ring-2 focus:ring-sigma-100" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700">Grade</label>
              <input value={grade} onChange={(e) => setGrade(e.target.value)} disabled={isBatchQuestionMode} placeholder="e.g., 12" className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-100 focus:border-sigma-500 focus:outline-none focus:ring-2 focus:ring-sigma-100" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700">Total Marks</label>
              <input type="number" value={totalMarks} onChange={(e) => setTotalMarks(e.target.value)} placeholder="e.g., 150" className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-sigma-500 focus:outline-none focus:ring-2 focus:ring-sigma-100" />
            </div>
          </div>
        </div>

        <button
          type="submit"
          disabled={isDisabled}
          className={`w-full rounded-xl px-4 py-3 text-sm font-semibold text-white transition-colors ${
            isDisabled
              ? 'cursor-not-allowed bg-gray-300'
              : 'bg-sigma-600 hover:bg-sigma-700'
          }`}
        >
          {isSubmitting
            ? (markingType === 'manual' ? 'Preparing Session...' : 'Uploading and Starting...')
            : (markingType === 'manual' ? 'Start Manual Marking' : 'Start AI Marking')}
        </button>
      </fieldset>
    </form>
  );
};

export default MarkingUploadForm;
